import * as React from "react";
import {ApolloCatalogData, ApolloCatalogDataType, ImportedDataSearchFilter} from "./model";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AxiosError} from "axios";
import {Redirect} from "react-router-dom";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";

interface DataFolderTreeItemProperties {
  level: number;
  allFolders: ApolloCatalogData[];
  folder: ApolloCatalogData;
  filter: ImportedDataSearchFilter;
  onSelect: (folderId: string) => void;

  filterChanged(folderId: string);

  pathToExpand: ApolloCatalogData[];
}

interface DataFolderTreeItemState {
  expanded: boolean;
}

class DataFolderTreeItem extends React.Component<DataFolderTreeItemProperties & WithApiProperties, DataFolderTreeItemState> {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
    };
  }

  componentDidMount() {
    this.expandSelected();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.filter.folder === this.props.filter.folder) {
      return;
    }
    this.expandSelected();
  }

  render() {
    return (
        <div data-cy="folder-tree-item" data-cy-title={this.props.folder.title}>
          <div className={`dataFolderTreeItem ${this.isSelected() ? "dataFolderTreeItemSelected" : ""}`}>
             <span id={`folder_loading_${this.props.folder.id}`} className="dataFolderTreeItemIcon"
                   style={{display: "none", marginLeft: "10px"}}>
               <div className="loader"></div>
             </span>
            <span id={`folder_icon_${this.props.folder.id}`} data-cy="expand"
                  onClick={this.toggleExpand} className="dataFolderTreeItemIcon"
                  style={{display: "block", marginLeft: "10px"}}>
              {this.state.expanded && <LcdIcon icon="folder-open-unfill"/>}
              {!this.state.expanded && <LcdIcon icon="folder-unfill"/>}
            </span>
            <span data-cy="select" className="dataFolderTreeItemLink"
                  onClick={this.toggleSelect}>{this.props.folder.title}
            </span>

          </div>
          {this.state.expanded && <DataFolderTreeComponent
              level={this.props.level + 1}
              allFolders={this.props.allFolders}
              folderId={this.props.folder.id}
              isChild={true}
              filter={this.props.filter}
              filterChanged={this.props.filterChanged}
              pathToExpand={this.props.pathToExpand}
              api={this.props.api}
          />}
        </div>
    );
  }

  private expandSelected() {
    const folderToExpand = this.props.pathToExpand[this.props.level];

    if (!folderToExpand || folderToExpand.id !== this.props.folder.id) {
      return;
    }

    if (this.props.level === this.props.pathToExpand.length - 1) {
      return;
    }

    if (!this.state.expanded) {
      this.toggleExpand();
    }
  }

  private toggleExpand = () => {
    this.setState(prevState => ({expanded: !prevState.expanded}));
  };

  private toggleSelect = () => {
    if (this.isSelected()) {
      this.props.onSelect(null);
    } else {
      this.props.onSelect(this.props.folder.id);
    }
  };

  private isSelected = () => {
    return this.props.filter.folder === this.props.folder.id;
  };
}

interface DataFolderTreeOwnState {
  folders: any;
  loading?: boolean;
  error: AxiosError;
}

interface DataFolderTreeOwnProperties {
  level: number;
  allFolders: ApolloCatalogData[];
  folderId?: string;
  isChild?: boolean;
  filter: ImportedDataSearchFilter;

  filterChanged(folderId: string);

  pathToExpand: ApolloCatalogData[];
}

class DataFolderTreeComponent extends React.Component<DataFolderTreeOwnProperties & WithApiProperties, DataFolderTreeOwnState> {
  constructor(props) {
    super(props);
    this.state = {
      folders: [],
      error: null,
    };
  }

  componentDidMount() {
    if (this.props.isChild) {
      this.updateFolders(this.props.folderId);
    } else {
      this.setState({loading: false});
    }
  }

  render() {
    const {error} = this.state;
    const folders = this.getFolders();

    if (error) {
      if (error?.response.status === 401) {
        return <Redirect to="/login"/>;
      }
      return <ErrorDisplay error={error}/>;
    }

    return (
        <div data-cy="folder-tree-children"
             className={`dataFolderTree ${this.props.isChild ? "dataFolderTreeChild" : "dataFolderTreeRoot"}`}>

          {folders.map(f =>
              <DataFolderTreeItem level={this.props.level}
                                  key={f.id}
                                  allFolders={this.props.allFolders}
                                  folder={f}
                                  filter={this.props.filter}
                                  filterChanged={this.props.filterChanged}
                                  onSelect={this.onSelect}
                                  pathToExpand={this.props.pathToExpand}
                                  api={this.props.api}
              />
          )}
        </div>
    );
  }

  private getFolders() {
    const {allFolders, folderId} = this.props;
    if (!allFolders) {
      return null;
    }

    let list;
    if (folderId) {
      list = allFolders.filter(f => f.parent && f.parent.id === folderId);
    } else {
      list = allFolders.filter(f => f.parent === null);
    }
    return list.sort((a, b) => (a.title.localeCompare(b.title)));
  }

  private updateFolders(folderId) {
    const {allFolders, api} = this.props;
    this.setState({loading: true});
    document.getElementById(`folder_loading_${folderId}`).style.display = "block";
    document.getElementById(`folder_icon_${folderId}`).style.display = "none";

    api.getFolders(folderId)
        .then((folders) => {
          folders.forEach(data => {
            if (data.path.startsWith("folder=")) {
              if (allFolders.findIndex((folder) => folder.id === data.id) < 0) {
                allFolders.push({
                  dataType: ApolloCatalogDataType.Folder,
                  type: "folder",
                  id: data.id,
                      title: data.title ? data.title : data.name,
                      parent: allFolders.find(p => p.id === folderId)
                    });
                  }
                }
              });
          this.setState({loading: false});
          document.getElementById(`folder_loading_${folderId}`).style.display = "none";
          document.getElementById(`folder_icon_${folderId}`).style.display = "block";
            }
        )
        .catch((error) => {
          this.setState({error});
        })
  }

  private onSelect = (folderId: string) => {
    this.props.filterChanged(folderId);
  };
}

export const DataFolderTree = WithApi(DataFolderTreeComponent);